import React from 'react';
import {
  Box,
  IconButton,
  Popover,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import { ChromePicker, type ColorResult } from 'react-color';

import {
  describeColor,
  formatCmyk,
  formatHex,
  formatHsl,
  formatRgb,
} from './colorUtils';

interface Props {
  open: boolean;
  anchorEl: HTMLElement | null;
  /** The swatch's CSS color value. */
  color: string;
  label: string;
  onClose: () => void;
}

type ColorView = 'hex' | 'rgb' | 'hsl' | 'cmyk' | 'picker';

const VIEWS: { value: ColorView; label: string }[] = [
  { value: 'hex', label: 'HEX' },
  { value: 'rgb', label: 'RGB' },
  { value: 'hsl', label: 'HSL' },
  { value: 'cmyk', label: 'CMYK' },
  { value: 'picker', label: 'Picker' },
];

/**
 * Color inspector for a swatch (backend-independent). Parses the swatch's CSS
 * color value and lets the user view it in HEX / RGB / HSL / CMYK — all mapped
 * client-side — plus a `react-color` picker to explore around it. Read-only:
 * the actual selection is still set by clicking the swatch. Pantone is not
 * offered (it's a licensed system with no accurate open mapping).
 */
export const ColorModelPopover: React.FC<Props> = ({ open, anchorEl, color, label, onClose }) => {
  const [view, setView] = React.useState<ColorView>('hex');
  // `picked` is the hex the picker edits; seeded from the swatch's color so all
  // readouts (and the preview) stay consistent as the user explores.
  const [picked, setPicked] = React.useState<string | null>(null);
  const [copied, setCopied] = React.useState(false);

  // (Re)seed when a different swatch is inspected.
  React.useEffect(() => {
    const models = describeColor(color);
    setPicked(models?.hex ?? null);
    setView('hex');
  }, [color]);

  const models = describeColor(picked);
  const valueText = models
    ? view === 'rgb'
      ? formatRgb(models.rgb)
      : view === 'hsl'
        ? formatHsl(models.hsl)
        : view === 'cmyk'
          ? formatCmyk(models.cmyk)
          : formatHex(models.rgb)
    : color;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(valueText);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      /* clipboard unavailable — no-op */
    }
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      slotProps={{ paper: { sx: { p: 1.5, width: 248 } } }}
    >
      <Stack spacing={1}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              flexShrink: 0,
              bgcolor: models ? formatHex(models.rgb) : color,
              border: theme => `1px solid ${theme.palette.divider}`,
            }}
          />
          <Typography variant="subtitle2" noWrap title={label}>
            {label}
          </Typography>
        </Stack>

        {!models && (
          <Typography variant="caption" color="text.secondary">
            Unrecognized color value: <code>{color || '—'}</code>
          </Typography>
        )}

        {models && (
          <>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={view}
              onChange={(_e, v: ColorView | null) => v && setView(v)}
              sx={{ flexWrap: 'wrap' }}
            >
              {VIEWS.map(v => (
                <ToggleButton key={v.value} value={v.value} sx={{ px: 1, py: 0.25, fontSize: 11 }}>
                  {v.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>

            {view === 'picker' ? (
              <ChromePicker
                color={picked ?? '#000000'}
                disableAlpha
                onChange={(c: ColorResult) => setPicked(c.hex)}
                styles={{ default: { picker: { boxShadow: 'none', width: '100%' } } }}
              />
            ) : (
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  bgcolor: 'action.hover',
                  fontFamily: 'monospace',
                }}
              >
                <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                  {valueText}
                </Typography>
                <Tooltip title={copied ? 'Copied' : 'Copy'}>
                  <IconButton size="small" onClick={handleCopy} aria-label="Copy color value">
                    {copied ? (
                      <CheckIcon sx={{ fontSize: 15 }} color="success" />
                    ) : (
                      <ContentCopyIcon sx={{ fontSize: 15 }} />
                    )}
                  </IconButton>
                </Tooltip>
              </Stack>
            )}

            <Typography variant="caption" color="text.secondary">
              Read-only — selection is set by clicking the swatch.
            </Typography>
          </>
        )}
      </Stack>
    </Popover>
  );
};
